/* eslint-disable react-hooks/exhaustive-deps */
import { Path } from "@common/constans";
import { LoaderMode } from "@common/enum";
import { AirCheckinRequest, IAirCheckinRequest, IMessage } from "@common/models";
import Alert from "@components/general/Alert";
import { Dialog } from "@components/general/dialog";
import Button from "@components/general/form/Button";
import Form from "@components/general/form/Form";
import TextInput from "@components/general/form/TextInput";
import Icon from "@components/general/Icon";
import { IModelValidationMessage, IValidationError } from "@core/index";
import { AppAction, IAppAction } from "@misc/Action";
import { IAppState } from "@misc/State";
import { IOptions } from "@service/Api";
import Config from "@service/Config";
import { Action, getCheckinInfo } from "@service/proxy";
import Translate from "@service/Translate";
import { getAirport } from "@utilize/Format";
import { nameof, validationMessage } from "@utilize/Util";
import { FC, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router";

interface ICheckinLoginFormProps {
    orientation: "horizontal" | "vertical";
}

const CheckinLoginForm: FC<ICheckinLoginFormProps> = ({ orientation }) => {
    const { tr, re } = Translate;
    const dispatch = useDispatch();
    const { push } = useHistory();
    const query = new URLSearchParams(useLocation().search);
    const [showSearchLoading, setShowSearchLoading] = useState<boolean>(false);
    const [showNoRouteMessagePopup, setShowNoRouteMessagePopup] = useState<boolean>(false);
    const [validationError, setValidationError] = useState<IValidationError>({});
    const { airCheckinRequest, airports } = useSelector((state: IAppState) => ({ airCheckinRequest: state.airCheckinRequest, airports: state.airports }));
    const formErrorsMessages: IModelValidationMessage = {
        referenceId: {
            required: tr("err_record_required", `${tr("reservation_number")} / ${tr("ticket_number")}`),
            type: tr("err_record_force_digit", `${tr("reservation_number")} / ${tr("ticket_number")}`),
            pattern: tr("err_booking_reference")
        },
        flightNumber: {
            required: tr("err_record_required", "flight_no"),
            type: tr("err_invalid_flight_number"),
        },
    };

    useEffect(() => {
        dispatch<IAppAction>({ type: AppAction.Preloader, payload: { preloader: { mode: LoaderMode.Solid, show: true } } });
        if (query.get("flt")) {
            airCheckinRequest.flightNumber = query.get("flt") || undefined;
            airCheckinRequest.referenceId = query.get("ref") || undefined;
            onCheckinLoginClick(query.get("token") || undefined);
        } else {
            dispatch<IAppAction>({ type: AppAction.Preloader, payload: { preloader: { mode: LoaderMode.Solid, show: false } } });
        }
    }, [JSON.stringify(query)])

    const checkinRequestValidation = () => {
        let airCheckin = new AirCheckinRequest(airCheckinRequest);
        return airCheckin.validate();
    };

    const onCheckinLoginClick = (token?: string) => {
        const validationError = checkinRequestValidation();
        if (validationError)
            return setValidationError(validationError);
        const req: IOptions<IAirCheckinRequest> = {
            data: airCheckinRequest
        }
        if (token) {
            req.headers = { Token: token };
            req.captchaRequierd = false;
        }

        setShowSearchLoading(true);
        getCheckinInfo(Action.POST, req)
            .then((response) => {
                dispatch<IAppAction>({ type: AppAction.AirCheckinRequest, payload: { airCheckinRequest } });
                dispatch<IAppAction>({ type: AppAction.AirCheckinInfo, payload: { airCheckinInfo: response.content } });
                dispatch<IAppAction>({ type: AppAction.AirCheckinSelectedTravelers, payload: { airCheckinSelectedTravelers: [] } });
                push(Path.Checkin.SelectPassenger);
                dispatch<IAppAction>({ type: AppAction.Preloader, payload: { preloader: { show: false } } })
            })
            .catch((err) => {
                setShowSearchLoading(false);
                dispatch<IAppAction>({ type: AppAction.Preloader, payload: { preloader: { show: false } } })
                if (err?.messages?.find((msg: IMessage) => msg.code === "chk_00"))
                    setShowNoRouteMessagePopup(true);
            })
    };

    const onRemoveValidation = (name: string, value: any) => {
        if (validationError[name])
            delete validationError[name];
        setValidationError({ ...validationError });
    };

    const error = validationError ? validationMessage(formErrorsMessages, validationError) : {};

    return (
        Config.config.setting.checkinDisableUntil && new Date(Config.config.setting.checkinDisableUntil).getTime() > new Date().getTime() ?
            <Alert
                title={<div dangerouslySetInnerHTML={{ __html: Config.config.setting.checkinDisableMessage?.html ?? "" }} />}
                type="information"
                variant="contain"
            />
            :
            <Form className="check-in-login-form" onSubmit={showSearchLoading ? undefined : () => onCheckinLoginClick()}>
                <div className={`row flex spa-1 ${orientation}`}>
                    <div className={`${orientation === "horizontal" ? "col-1-3" : "col-1-1"} col-sm-1-1`}>
                        <TextInput
                            name={nameof<IAirCheckinRequest>("referenceId")}
                            dataSource={airCheckinRequest}
                            autoComplete="off"
                            dataMember={nameof<IAirCheckinRequest>("referenceId")}
                            label={tr("pnr_or_ticket_number")}
                            dir="ltr"
                            type="number"
                            required
                            readonly={showSearchLoading}
                            error={error.referenceId}
                            onChange={onRemoveValidation}
                        />
                    </div>
                    <div className={`${orientation === "horizontal" ? "col-1-3" : "col-1-1"} col-sm-1-1`}>
                        <TextInput
                            name={nameof<IAirCheckinRequest>("flightNumber")}
                            dataSource={airCheckinRequest}
                            autoComplete="off"
                            dataMember={nameof<IAirCheckinRequest>("flightNumber")}
                            label={tr("flight_no")}
                            inputLanguage="en"
                            dir="ltr"
                            prefix="W5"
                            pad={{ type: "start", count: 4, character: "0" }}
                            type="tel"
                            required
                            readonly={showSearchLoading}
                            error={error.flightNumber}
                            onChange={onRemoveValidation}
                        />
                    </div>
                    <div className={`${orientation === "horizontal" ? "col-1-3" : "col-1-1"} col-sm-1-1`}>
                        <Button
                            text={tr("search")}
                            type="submit"
                            variant="contained"
                            size="large"
                            loading={showSearchLoading}
                            color="danger"
                        />
                    </div>
                </div>
                <Dialog
                    show={showNoRouteMessagePopup}
                    className="survey-submit-form-success-modal"
                    animate="fade"
                    title={tr("message")}
                    onHiding={() => setShowNoRouteMessagePopup(false)}
                >
                    <section className="content-section">
                        <p>{re("online_checkin_alert_enable_airports")}</p>
                        <div className="row flex flex-center spa-2">
                            {
                                airports.filter(airport => airport.onlineCheckin).map((a, i) =>
                                    <div key={i} className="col-1-3 city-item flex flex-center">
                                        <Icon name="icon-double-left" size="20px" color="primary" />
                                        {getAirport(a.code).cityName}
                                    </div>
                                )
                            }
                        </div>
                    </section>
                </Dialog>
            </Form>
    );
};

export default CheckinLoginForm;